// SCR-STAFF-900 — Setup Wizard (`/setup`؛ US-PLT-050؛ AP-PLT-04): Stepper عمودی ۱۰ مرحله‌ای (AC-PLT-050.1) با وضعیت (کامل، جاری، ردشده،
// به‌زودی)، ادامه از آخرین مرحله (AC-PLT-050.2)، رد شدن مرحله غیرالزامی (AC-PLT-050.3). هر مرحله همان صفحه تنظیمات ماژول مالک را
// (بدون کپی) درون پنل با راهنمای کوتاه و «بعدی» نشان می‌دهد؛ صفحه جزئیات (خدمت، کارکن) هم درون پنل باز می‌شود.
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { unwrap, type OnboardingStepView, type OnboardingView } from '@phoenix/api-client';
import { RequireAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { EmbeddedPage, ErrorState, faDigits, LoadingState, PageHeader, StatusChip } from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { DetailNavigationProvider } from '../../layout/detailNavigation';
import { leafItems, SUBSCRIPTION_PATH } from '../../navigation';
import { ServiceDetailPage } from '../catalog/ServiceDetailPage';
import { ServicesPage } from '../catalog/ServicesPage';
import { SpecialtyPacksPage } from '../configuration/SpecialtyPacksPage';
import { StaffDetailPage } from '../iam/StaffDetailPage';
import { StaffListPage } from '../iam/StaffListPage';
import { RoomsPage } from '../organization/RoomsPage';
import { SchedulesSettingsPage } from '../scheduling/SchedulesSettingsPage';
import { BranchesPage } from '../settings/BranchesPage';
import { CenterProfilePage } from '../settings/CenterProfilePage';
import { ImportPage } from '../settings/import/ImportPage';
import { ChannelsPage } from '../messaging/ChannelsPage';
import { TemplatesPage } from '../messaging/TemplatesPage';
import { ChecklistPage } from '../settings/ChecklistPage';
import {
  incompleteRequired,
  onboardingKeys,
  SCR_STAFF_900,
  STEP_TONE,
  useOnboarding,
  useRefreshOnboardingOnMutations,
} from './common';

/** صفحه تنظیمات منو با همان Permission منو (RequireAccess). */
function MenuPage({ menuId, children }: { menuId: string; children: ReactNode }) {
  const item = leafItems().find((candidate) => candidate.id === menuId);
  return (
    <RequireAccess
      permission={item?.permission}
      anyPermission={item?.anyPermission}
      feature={item?.feature}
      upgradePath={SUBSCRIPTION_PATH}
    >
      {children}
    </RequireAccess>
  );
}

/** جزئیات بازشده درون پنل (مسیر /settings/services/:id یا /settings/staff/:id). */
type InlineDetail = { kind: 'service' | 'member'; id: string } | null;

const DETAIL_PATTERNS: readonly { kind: 'service' | 'member'; pattern: RegExp }[] = [
  { kind: 'service', pattern: /^\/settings\/services\/([0-9a-f-]{36})$/ },
  { kind: 'member', pattern: /^\/settings\/staff\/([0-9a-f-]{36})$/ },
];

function StepPanelContent({
  stepKey,
  detail,
  onBack,
}: {
  stepKey: string;
  detail: InlineDetail;
  onBack: () => void;
}) {
  const { t } = useTranslation('plt');
  const back = (labelKey: string) => (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={onBack}
      sx={{ alignSelf: 'flex-start' }}
      data-testid="setup-detail-back"
    >
      {t(labelKey)}
    </Button>
  );
  switch (stepKey) {
    case 'center':
      return (
        <MenuPage menuId="center">
          <CenterProfilePage />
        </MenuPage>
      );
    case 'branches':
      return (
        <Stack spacing={3}>
          <MenuPage menuId="branches">
            <BranchesPage />
          </MenuPage>
          <MenuPage menuId="rooms">
            <RoomsPage />
          </MenuPage>
        </Stack>
      );
    // AP-CFG-01: مرحله ۳ «انتخاب بسته تخصصی» همان صفحه بسته‌ها (پیش‌نمایش و اعمال — FLOW-16).
    case 'specialty_pack':
      return (
        <MenuPage menuId="specialty-packs">
          <SpecialtyPacksPage />
        </MenuPage>
      );
    case 'services':
      return detail?.kind === 'service' ? (
        <Stack spacing={2}>
          {back('plt:setup.back_to_services')}
          <ServiceDetailPage serviceId={detail.id} />
        </Stack>
      ) : (
        <MenuPage menuId="services">
          <ServicesPage />
        </MenuPage>
      );
    case 'staff':
      return detail?.kind === 'member' ? (
        <Stack spacing={2}>
          {back('plt:setup.back_to_staff')}
          <StaffDetailPage membershipId={detail.id} />
        </Stack>
      ) : (
        <MenuPage menuId="staff">
          <StaffListPage />
        </MenuPage>
      );
    case 'schedule':
      return (
        <MenuPage menuId="schedules">
          <SchedulesSettingsPage />
        </MenuPage>
      );
    // AP-MSG-01: مرحله ۸ «قالب‌های پیام و یادآوری» — ذخیره تنظیمات پیام یا ویرایش یک قالب مرحله را کامل می‌کند.
    case 'messages':
      return (
        <Stack spacing={3}>
          <MenuPage menuId="messaging-channels">
            <ChannelsPage />
          </MenuPage>
          <MenuPage menuId="messaging-templates">
            <TemplatesPage />
          </MenuPage>
        </Stack>
      );
    case 'import':
      return (
        <MenuPage menuId="import">
          <ImportPage />
        </MenuPage>
      );
    // AP-PLT-05: مرحله Go-Live همان چک‌لیست راه‌اندازی با اعلام شروع کار است (SCR-STAFF-901).
    case 'go_live':
      return (
        <MenuPage menuId="checklist">
          <ChecklistPage />
        </MenuPage>
      );
    default:
      return (
        <Alert severity="info" data-testid="setup-coming-soon">
          {t('plt:setup.coming_soon_help')}
        </Alert>
      );
  }
}

function StepStatus({ step }: { step: OnboardingStepView }) {
  const { t } = useTranslation('plt');
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      <span>{t(`plt:setup.steps.${step.key}.title`)}</span>
      {!step.isRequired && step.isAvailable && (
        <Typography component="span" variant="caption" color="text.secondary">
          {t('plt:setup.optional')}
        </Typography>
      )}
      <StatusChip tone={STEP_TONE[step.state]} labelKey={`plt:setup.state.${step.state}`} />
    </Stack>
  );
}

function WizardBody({ view }: { view: OnboardingView }) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(view.currentStep);
  const [detail, setDetail] = useState<InlineDetail>(null);
  const started = useRef(view.started);
  const panelHeading = useRef<HTMLHeadingElement>(null);

  const apply = useCallback(
    (updated: OnboardingView) => queryClient.setQueryData(onboardingKeys.view(session?.tenantId), updated),
    [queryClient, session?.tenantId],
  );

  const move = useMutation({
    mutationFn: (body: { stepKey: string; finish: boolean }) =>
      unwrap(tenantApi.PUT('/api/v1/platform/onboarding/current-step', { body })),
    onSuccess: apply,
  });
  const skip = useMutation({
    mutationFn: (key: string) =>
      unwrap(
        tenantApi.POST('/api/v1/platform/onboarding/steps/{key}:skip', {
          params: { path: { key } },
        }),
      ),
    onSuccess: apply,
  });

  // ورود نخست: Wizard «شروع‌شده» ثبت می‌شود تا هدایت خودکار خانه تکرار نشود.
  useEffect(() => {
    if (!started.current) {
      started.current = true;
      move.mutate({ stepKey: view.currentStep, finish: false });
    }
    // فقط یک بار در نخستین نمایش.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const available = useMemo(() => view.steps.filter((step) => step.isAvailable), [view.steps]);
  const step = view.steps.find((candidate) => candidate.key === selected) ?? view.steps[0]!;
  const nextStep = available.find((candidate) => candidate.order > step.order);
  const previousStep = [...available].reverse().find((candidate) => candidate.order < step.order);
  const missing = incompleteRequired(view);

  const go = (key: string) => {
    setSelected(key);
    setDetail(null);
    if (view.steps.find((candidate) => candidate.key === key)?.isAvailable) {
      move.mutate({ stepKey: key, finish: false });
    }
    window.requestAnimationFrame(() => panelHeading.current?.focus());
  };

  const openDetail = useCallback((path: string) => {
    for (const { kind, pattern } of DETAIL_PATTERNS) {
      const match = pattern.exec(path);
      if (match?.[1]) {
        setDetail({ kind, id: match[1] });
        return true;
      }
    }
    return false;
  }, []);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
      <Card variant="outlined" sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" data-testid="setup-progress">
            {t('plt:setup.progress', {
              done: faDigits(Number(view.completedSteps)),
              total: faDigits(Number(view.availableSteps)),
            })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(Number(view.completedSteps) / Math.max(1, Number(view.availableSteps))) * 100}
            sx={{ my: 1.5 }}
            aria-label={t('plt:setup.progress_label')}
          />
          <Stepper
            orientation="vertical"
            nonLinear
            activeStep={view.steps.indexOf(step)}
            component="nav"
            aria-label={t('plt:setup.steps_label')}
          >
            {view.steps.map((candidate) => (
              <Step key={candidate.key} completed={candidate.isComplete}>
                <StepButton
                  onClick={() => go(candidate.key)}
                  data-testid={`setup-step-${candidate.key}`}
                  data-state={candidate.state}
                  aria-current={candidate.key === step.key ? 'step' : undefined}
                >
                  <StepStatus step={candidate} />
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }} data-testid="setup-panel" data-step={step.key}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Typography variant="h2" component="h2" tabIndex={-1} ref={panelHeading} sx={{ outline: 'none' }}>
              {t('plt:setup.step_heading', {
                order: faDigits(step.order),
                title: t(`plt:setup.steps.${step.key}.title`),
              })}
            </Typography>
            <StatusChip tone={STEP_TONE[step.state]} labelKey={`plt:setup.state.${step.state}`} />
          </Stack>
          <Alert severity="info" icon={false} data-testid="setup-guidance">
            {t(`plt:setup.steps.${step.key}.help`)}
          </Alert>

          {step.isAvailable ? (
            <Card variant="outlined">
              <CardContent>
                <DetailNavigationProvider open={openDetail}>
                  <EmbeddedPage>
                    <StepPanelContent stepKey={step.key} detail={detail} onBack={() => setDetail(null)} />
                  </EmbeddedPage>
                </DetailNavigationProvider>
              </CardContent>
            </Card>
          ) : (
            <StepPanelContent stepKey={step.key} detail={null} onBack={() => setDetail(null)} />
          )}

          {(move.error ?? skip.error) && <ErrorState compact error={move.error ?? skip.error} />}
          {view.completedAt && (
            <Alert
              severity="success"
              data-testid="setup-finished"
              action={
                <Button component={RouterLink} to="/today" color="inherit">
                  {t('plt:setup.go_today')}
                </Button>
              }
            >
              {t('plt:setup.finished')}
            </Alert>
          )}
          {!nextStep && missing.length > 0 && (
            <Alert severity="warning" data-testid="setup-missing">
              {t('plt:setup.missing', {
                steps: missing
                  .map((key) => t(`plt:setup.steps.${key}.title`))
                  .join(t('plt:setup.list_separator')),
              })}
            </Alert>
          )}

          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <Button
              disabled={!previousStep}
              onClick={() => previousStep && go(previousStep.key)}
              data-testid="setup-previous"
            >
              {t('plt:setup.previous')}
            </Button>
            <Stack direction="row" spacing={1}>
              {step.isAvailable && !step.isRequired && !step.isComplete && !step.isSkipped && (
                <Button
                  variant="outlined"
                  disabled={skip.isPending}
                  onClick={() => skip.mutate(step.key)}
                  data-testid="setup-skip"
                >
                  {t('plt:setup.skip')}
                </Button>
              )}
              {nextStep ? (
                <Button variant="contained" onClick={() => go(nextStep.key)} data-testid="setup-next">
                  {t('plt:setup.next')}
                </Button>
              ) : (
                !view.completedAt && (
                  <Button
                    variant="contained"
                    disabled={missing.length > 0 || move.isPending}
                    onClick={() =>
                      move.mutate({ stepKey: step.isAvailable ? step.key : view.currentStep, finish: true })
                    }
                    data-testid="setup-finish"
                  >
                    {t('plt:setup.finish')}
                  </Button>
                )
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}

export function SetupWizardPage() {
  const { session } = useTenantSession();
  const onboarding = useOnboarding({ refetchInterval: 5000 });
  useRefreshOnboardingOnMutations(session?.tenantId);

  return (
    <Stack data-screen-id={SCR_STAFF_900} spacing={2}>
      <PageHeader titleKey="plt:setup.title" subtitleKey="plt:setup.subtitle" />
      {onboarding.isPending ? (
        <LoadingState rows={6} />
      ) : onboarding.error ? (
        <ErrorState error={onboarding.error} onRetry={() => void onboarding.refetch()} />
      ) : onboarding.data ? (
        <WizardBody view={onboarding.data} />
      ) : null}
    </Stack>
  );
}
